'use client'

import React, { useEffect, useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { Box, Flex, Text, Title } from '@mantine/core'
import { staggerContainer, staggerItem } from '@/lib/animations'
import { BaseRenderedService, RenderedService } from '@/types/interfaces/service-item'
import { ServiceItemCardType } from '@/types/enums/ServiceItem'
import { PageLocation } from '@nile/mantine-theme/analytics'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import StoreSkuCarousel from '@/components/Refresh/StoreSkuCarousel/StoreSkuCarousel'
import { CardSlider } from '@/components/Refresh/CardSlider/CardSlider'
import { CardSliderCard } from '@/components/Refresh/CardSlider/CardSliderCard'
import { FullBleedWrapper } from '../FullBleedWrapper'
import TabbedCard from '@/components/Refresh/Cards/TabbedCard/TabbedCard'
import ProductImageCard from '@/components/Refresh/Cards/ProductImageCard/ProductImageCard'
import { isMedicationSkuCategory } from '@/components/Refresh/SearchResults/searchResultsUtils'
import FeaturedCarouselCardV2 from './FeaturedCarouselCardV2'
import SupportCarouselCardV2 from './SupportCarouselCardV2'
import { RefreshCtaButton } from '../RefreshCtaButton'
import { RightArrowIcon } from '@/components/Icons/ClinicianHiring/RightArrowIcon'
import { getCategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import type { CategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import SpecialistsBg from '@/assets/images/refresh/specialists-bg.webp'
import MedicationImg from '@/assets/images/refresh/support_widget/medication.webp'
import LabsImg from '@/assets/images/refresh/support_widget/labs.webp'
import DermatologyImg from '@/assets/images/refresh/support_widget/dermatology.webp'
import TravelImg from '@/assets/images/refresh/support_widget/travel.webp'
import FluImg from '@/assets/images/refresh/support_widget/flu.webp'
import EverydayImg from '@/assets/images/refresh/support_widget/everyday.webp'

const SUPPORT_IMAGE_MAP: Record<string, typeof MedicationImg> = {
    medication: MedicationImg,
    labs: LabsImg,
    dermatology: DermatologyImg,
    travel: TravelImg,
    flu: FluImg,
    everyday: EverydayImg,
}

interface CarouselV2Props {
    categoryName: string
    service?: RenderedService
    pageLocation?: PageLocation
    additionalInformation?: Record<string, unknown>
    /** Pre-fetched category data. When provided, uses wholeBodyCarousel instead of getCategoryPageData. */
    categoryData?: CategoryPageData | null
    disableWholeBodyLayout?: boolean
}

export default function CarouselV2({
    categoryName,
    service,
    pageLocation = PageLocation.CATEGORY,
    additionalInformation = { categoryName },
    categoryData: categoryDataProp,
    disableWholeBodyLayout = false,
}: CarouselV2Props) {
    const containerRef = useRef<HTMLDivElement>(null)
    const { isSm } = useBreakpoint()
    const [cardStartX, setCardStartX] = useState(0)
    const base = service as BaseRenderedService | undefined

    useEffect(() => {
        const el = containerRef.current
        const updateStartX = () => {
            if (!el) return
            const rect = el.getBoundingClientRect()
            const padding = isSm ? 16 : 24
            setCardStartX(Math.max(0, rect.left + padding))
        }
        updateStartX()
        window.addEventListener('resize', updateStartX)
        const observer = new ResizeObserver(updateStartX)
        if (el) observer.observe(el)
        return () => {
            window.removeEventListener('resize', updateStartX)
            observer.disconnect()
        }
    }, [isSm])
    const title = base?.title
    const subtitle = base?.subtitle
    const button = base?.button
    const services = base?.services ?? []
    const cardType = base?.cardType ?? ServiceItemCardType.serviceItem
    const isProductImageCard = cardType === ServiceItemCardType.productImageCard

    const wholeBodyData = disableWholeBodyLayout
        ? null
        : (categoryDataProp?.wholeBodyCarousel ?? getCategoryPageData(categoryName, 'wholeBodyCarousel'))

    if (wholeBodyData) {
        const displayTitle = title ?? ''
        const displaySubtitle = subtitle ?? ''

        const supportItems = wholeBodyData.otherCards
            .map((card) => {
                const image = card.image ?? (card.imagePath ? SUPPORT_IMAGE_MAP[card.imagePath] : undefined)
                const buttonLink = card.url
                if (!image || !buttonLink) return null
                return {
                    id: card.id,
                    label: card.label,
                    description: card.description,
                    image,
                    buttonLink,
                    buttonText: 'Browse' as const,
                }
            })
            .filter((item): item is NonNullable<typeof item> => item != null)

        const firstService = services.length > 0 ? services[0] : undefined
        const showFirstCard = !!firstService

        return (
            <Box
                ref={containerRef}
                maw={1440}
                mx="auto"
                w="100%"
                mt={48}
                px={{ base: 16, sm: 24 }}
                style={{ overflow: 'visible' }}
            >
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-80px 0px' }}
                    variants={staggerContainer}
                    style={{ width: '100%' }}
                >
                    <Flex direction="column" align="flex-start" mb={isSm ? 24 : 32} gap="xs">
                        <motion.div variants={staggerItem}>
                            <Title
                                order={2}
                                c="gray.9"
                                fz={36}
                                fw="var(--weight-display-large, 400)"
                                lh="110%"
                                lts={-1.08}
                                m={0}
                            >
                                {displayTitle}
                            </Title>
                        </motion.div>
                        <motion.div variants={staggerItem}>
                            <Text size="lg" fw={450} lh="130%" c="gray.6" m={0} maw={790}>
                                {displaySubtitle}
                            </Text>
                        </motion.div>
                    </Flex>
                </motion.div>
                <FullBleedWrapper mb={48}>
                    <CardSlider gap={24} startX={cardStartX} endGap={96} useTranslateForStartX initialPosition="end">
                        {showFirstCard && (
                            <CardSliderCard key="featured">
                                <FeaturedCarouselCardV2
                                    data={
                                        wholeBodyData.firstCard ?? {
                                            title: wholeBodyData.title ?? displayTitle,
                                            description: wholeBodyData.subtitle ?? displaySubtitle,
                                        }
                                    }
                                    bgImage={wholeBodyData.firstCard?.bgImage ?? SpecialistsBg}
                                    service={firstService}
                                />
                            </CardSliderCard>
                        )}
                        {supportItems.map((item, index) => (
                            <CardSliderCard key={`${item.id}-${index}`}>
                                <SupportCarouselCardV2 item={item} />
                            </CardSliderCard>
                        ))}
                    </CardSlider>
                </FullBleedWrapper>
                {wholeBodyData.footerNote && (
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-80px 0px' }}
                        variants={staggerContainer}
                        style={{ width: '100%' }}
                    >
                        <Text
                            size="sm"
                            fw={450}
                            lh="130%"
                            c="gray.6"
                            maw={790}
                            mt={{ base: -16, sm: 24 }}
                            mb={{ base: 32, sm: 24 }}
                            dangerouslySetInnerHTML={{ __html: wholeBodyData.footerNote }}
                        />
                    </motion.div>
                )}
            </Box>
        )
    }

    if (!services.length) {
        return null
    }

    return (
        <Box ref={containerRef} maw={1440} mx="auto" w="100%" px={{ base: 16, sm: 24 }} style={{ overflow: 'visible' }}>
            {(title || subtitle || button) && (
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-80px 0px' }}
                    variants={staggerContainer}
                    style={{ width: '100%' }}
                >
                    <Flex
                        direction={{ base: 'column', sm: button ? 'row' : 'column' }}
                        justify={{ sm: button ? 'space-between' : 'flex-start' }}
                        align={{ base: 'flex-start', sm: button ? 'center' : 'flex-start' }}
                        mb={isSm ? 24 : 32}
                        gap="xs"
                    >
                        <Flex direction="column" gap="xs">
                            {title && (
                                <motion.div variants={staggerItem}>
                                    <Title
                                        order={2}
                                        c="gray.9"
                                        fz={36}
                                        fw="var(--weight-display-large, 400)"
                                        lh="110%"
                                        lts={-1.08}
                                        m={0}
                                    >
                                        {title}
                                    </Title>
                                </motion.div>
                            )}
                            {subtitle && (
                                <motion.div variants={staggerItem}>
                                    <Text size="lg" fw={450} lh="130%" c="gray.7" m={0} maw={790}>
                                        {subtitle}
                                    </Text>
                                </motion.div>
                            )}
                        </Flex>
                        {button && !isSm && (
                            <motion.div variants={staggerItem}>
                                <RefreshCtaButton
                                    href={button.link}
                                    variant="primary"
                                    rightSection={<RightArrowIcon stroke="white" />}
                                >
                                    {button.text}
                                </RefreshCtaButton>
                            </motion.div>
                        )}
                    </Flex>
                </motion.div>
            )}
            {cardType === ServiceItemCardType.serviceItem ? (
                <StoreSkuCarousel
                    services={services}
                    cardType={cardType}
                    pageLocation={pageLocation}
                    additionalInformation={additionalInformation}
                />
            ) : (
                <FullBleedWrapper mb={48}>
                    <CardSlider gap={24} startX={cardStartX} endGap={96} useTranslateForStartX initialPosition="end">
                        {services.map((item, index) => (
                            <CardSliderCard key={`${item.id}-${index}`}>
                                {isProductImageCard && isMedicationSkuCategory(item.category) ? (
                                    <ProductImageCard
                                        product={item}
                                        index={index}
                                        pageLocation={pageLocation}
                                        additionalInformation={additionalInformation}
                                    />
                                ) : (
                                    <TabbedCard
                                        product={item}
                                        index={index}
                                        pageLocation={pageLocation}
                                        additionalInformation={additionalInformation}
                                    />
                                )}
                            </CardSliderCard>
                        ))}
                    </CardSlider>
                </FullBleedWrapper>
            )}
        </Box>
    )
}
